import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { blogPostQueryOptions } from "@/api-queries/blog";
import { BlogRenderer, useBlogRendererReady } from "@/components/blog/BlogRenderer";
import "../../components/blog/editor.css";
import { CaretRight, ArrowRight } from "@phosphor-icons/react";
import { formatDate } from "@/lib/format";
import { readingMinutes } from "@/lib/reading-time";
import { isProblemTag } from "@/lib/matching/taxonomy";
import { isBlogCategory, BLOG_CATEGORY_LABELS } from "@/lib/blog-categories";
import { Img } from "@/components/ui/img";
import { GeneratedCover } from "@/components/blog/GeneratedCover";
import { AuthorAvatar } from "@/components/blog/AuthorAvatar";
import { BlogCard } from "@/components/blog/BlogCard";
import { ShareButton } from "@/components/blog/ShareButton";

export const Route = createFileRoute("/blog/$slug")({
	component: BlogPostPage,
});

export function getAuthorProfileLink(
	therapistProfileId?: string | null,
	therapistProfileSlug?: string | null,
) {
	const key = therapistProfileSlug ?? therapistProfileId;
	return key ? `/therapists/${key}` : null;
}

function BlogPostPage() {
	const { t } = useTranslation();
	const { slug } = Route.useParams();
	const { data: post, isLoading, error } = useQuery(blogPostQueryOptions(slug));
	const rendererReady = useBlogRendererReady();

	if (isLoading || (post && !rendererReady)) {
		return (
			<div className="min-h-screen">
				<div className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
					<div className="animate-pulse space-y-6">
						<div className="h-4 w-48 rounded bg-muted" />
						<div className="h-10 w-full rounded bg-muted" />
						<div className="h-4 w-32 rounded bg-muted" />
						<div className="aspect-video rounded-xl bg-muted" />
						<div className="space-y-3">
							<div className="h-4 w-full rounded bg-muted" />
							<div className="h-4 w-full rounded bg-muted" />
							<div className="h-4 w-3/4 rounded bg-muted" />
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (error || !post) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<div className="text-center">
					<h1
						className="text-2xl font-bold text-foreground"
						style={{ fontFamily: "var(--font-heading)" }}
					>
						{t("blogPost.notFound")}
					</h1>
					<p className="mt-2 text-muted-foreground">{t("blogPost.notFoundDesc")}</p>
					<Link
						to="/blog"
						className="mt-4 inline-block text-sm font-medium text-primary hover:underline"
					>
						{t("blogPost.backToBlog")}
					</Link>
				</div>
			</div>
		);
	}

	const authorProfileLink = getAuthorProfileLink(post.therapistProfileId, post.therapistProfileSlug);
	const authorName = post.authorName || t("blogPost.unknownAuthor");
	const minutes = readingMinutes(post.content);
	const category: unknown = post.category;
	const categoryLabel = isBlogCategory(category) ? BLOG_CATEGORY_LABELS[category] : null;
	const showTherapistCta = isProblemTag(category);
	const related: any[] = Array.isArray(post.related) ? post.related : [];

	return (
		<div className="min-h-screen" style={{ background: "oklch(98% 0.005 84)" }}>
			{/* Full-bleed hero */}
			<header className="relative flex min-h-[60vh] items-end overflow-hidden">
				<div className="absolute inset-0">
					{post.coverImageUrl ? (
						<Img src={post.coverImageUrl} alt={post.title} sizes="100vw" className="h-full w-full object-cover" />
					) : (
						<GeneratedCover slug={post.slug} category={post.category} className="h-full w-full object-cover" />
					)}
				</div>
				<div
					className="absolute inset-0"
					style={{
						background:
							"linear-gradient(to top, rgba(15,25,40,.82), rgba(15,25,40,.08) 62%)",
					}}
				/>
				<div className="relative mx-auto w-full max-w-3xl px-4 pb-28 pt-24 text-white sm:px-6">
					{/* Breadcrumb */}
					<nav className="mb-5 flex items-center gap-1 text-sm text-white/70">
						<Link to="/" className="transition-colors hover:text-white">
							{t("blogPost.breadcrumbHome")}
						</Link>
						<CaretRight className="size-3" />
						<Link to="/blog" className="transition-colors hover:text-white">
							{t("blogPost.breadcrumbBlog")}
						</Link>
					</nav>
					{categoryLabel && (
						<Link
							to="/blog"
							search={{ cat: post.category }}
							className="inline-block rounded-full bg-white/20 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em] backdrop-blur-sm transition-colors hover:bg-white/30"
						>
							{categoryLabel}
						</Link>
					)}
					<h1
						className="mt-4 text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl"
						style={{ fontFamily: "var(--font-heading)" }}
					>
						{post.title}
					</h1>
					<div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-white/85">
						<span>{authorName}</span>
						<span aria-hidden>·</span>
						<span>{t("blogPost.readingTime", { count: minutes })}</span>
						<span aria-hidden>·</span>
						<time>{formatDate(post.publishedAt || post.createdAt)}</time>
					</div>
				</div>
			</header>

			{/* White article sheet */}
			<article className="relative z-10 mx-auto -mt-14 max-w-3xl rounded-t-3xl bg-white px-5 py-10 shadow-[0_-12px_40px_rgba(0,0,0,.06)] sm:px-12 sm:py-14">
				{/* Author + share */}
				<div className="flex flex-wrap items-center justify-between gap-4 border-y border-border py-4">
					<div className="flex items-center gap-3">
						<AuthorAvatar name={post.authorName} image={post.authorImage} size={40} />
						<div>
							{authorProfileLink ? (
								<Link
									to={authorProfileLink}
									className="text-sm font-medium text-foreground transition-colors hover:text-primary hover:underline"
								>
									{authorName}
								</Link>
							) : (
								<div className="text-sm font-medium text-foreground">{authorName}</div>
							)}
							{post.authorRole === "therapist" && (
								<div className="text-xs text-muted-foreground">{t("blogPost.therapistRole")}</div>
							)}
						</div>
					</div>
					<ShareButton
						url={`${typeof window !== "undefined" ? window.location.origin : ""}/blog/${slug}`}
						title={post.title}
					/>
				</div>

				{/* Content */}
				<div className="mt-10">
					<BlogRenderer content={post.content} className="ProseMirror" />
				</div>

				{/* CTA: find a therapist for this topic (only for therapy-related categories) */}
				{showTherapistCta && categoryLabel && (
					<Link
						to="/therapists"
						search={{ problemTag: post.category }}
						className="mt-12 flex items-center justify-between gap-4 rounded-2xl px-6 py-6 text-white transition-transform hover:-translate-y-0.5"
						style={{ background: "linear-gradient(120deg, oklch(37.12% 0.059 186.62), oklch(35% 0.06 250))" }}
					>
						<div>
							<div className="text-xs font-semibold uppercase tracking-[0.1em] opacity-80">
								{t("blogPost.ctaKicker")}
							</div>
							<div className="mt-1 text-lg font-bold" style={{ fontFamily: "var(--font-heading)" }}>
								{t("blogPost.findTherapistCta", { topic: categoryLabel })}
							</div>
						</div>
						<ArrowRight className="size-6 shrink-0" weight="bold" />
					</Link>
				)}

				{/* Author card */}
				<div className="mt-12 rounded-xl border border-border bg-card p-6">
					<div className="flex items-start gap-4">
						<AuthorAvatar name={post.authorName} image={post.authorImage} size={56} />
						<div>
							{authorProfileLink ? (
								<Link
									to={authorProfileLink}
									className="font-semibold text-foreground transition-colors hover:text-primary hover:underline"
									style={{ fontFamily: "var(--font-heading)" }}
								>
									{authorName}
								</Link>
							) : (
								<div className="font-semibold text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
									{authorName}
								</div>
							)}
							{post.authorRole === "therapist" && (
								<div className="mt-0.5 text-sm text-muted-foreground">{t("blogPost.therapistRole")}</div>
							)}
							{authorProfileLink && (
								<Link
									to={authorProfileLink}
									className="mt-2 inline-block text-sm font-medium text-primary hover:underline"
								>
									{t("blogPost.viewTherapistProfile")}
								</Link>
							)}
						</div>
					</div>
				</div>
			</article>

			{/* Related posts */}
			{related.length > 0 && (
				<section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
					<h2
						className="mb-6 text-2xl font-bold text-foreground"
						style={{ fontFamily: "var(--font-heading)" }}
					>
						{t("blogPost.relatedTitle")}
					</h2>
					<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
						{related.map((r) => (
							<BlogCard key={r.id} post={r} hideAuthor />
						))}
					</div>
				</section>
			)}
		</div>
	);
}
